import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import Button from '../Button';
import Icon from '../Icon';
import {PrinterDialogFrame as DialogFrame, DialogButtons} from '../PrinterPickerDialog';
import TextInput from './TextInput';

// A scanned code can still be typed, or it can be scanned in from the camera or a handheld scanner like it is on mobile.
function ScannedCodeInput(props) {
  const {onChange} = props;
  const [scanning, setScanning] = useState(false);

  const handleScanned = code => {
    setScanning(false);
    onChange([code], true);
  };

  return (
    <>
      <TextInput {...props} />
      <ScanButton secondary onClick={() => setScanning(true)}>
        <Icon name="camera" /> Scan a QR or barcode
      </ScanButton>
      {scanning && <ScannerDialog onScanned={handleScanned} onCancel={() => setScanning(false)} />}
    </>
  );
}

const CAMERA_STATUS = {
  starting: 'Starting the camera…',
  scanning: 'Hold the code up to the camera.',
  unavailable: 'The camera could not be started. Check the browser has permission to use it, or use a handheld scanner.'
};

function ScannerDialog({onScanned, onCancel}) {
  const videoRef = useRef(null);
  // kept in a ref so a re-render of the answer editor doesn't restart the camera
  const onScannedRef = useRef(onScanned);
  onScannedRef.current = onScanned;
  const [cameraStatus, setCameraStatus] = useState('starting');
  const [handheldCode, setHandheldCode] = useState('');

  // The decoder is only loaded once someone scans, so it stays out of the main bundle.
  useEffect(() => {
    let controls = null;
    let closed = false;

    (async () => {
      try {
        const {BrowserMultiFormatReader} = await import('@zxing/browser');
        const reader = new BrowserMultiFormatReader();
        const started = await reader.decodeFromConstraints(
          {video: {facingMode: 'environment'}},
          videoRef.current,
          (result, error, scanControls) => {
            if (result && !closed) {
              closed = true;
              scanControls.stop();
              onScannedRef.current(result.getText());
            }
          }
        );

        // the dialog may have been closed while the camera was starting
        if (closed) {
          started.stop();
          return;
        }

        controls = started;
        setCameraStatus('scanning');
      }
      catch (e) {
        if (!closed) {
          setCameraStatus('unavailable');
        }
      }
    })();

    return () => {
      closed = true;
      if (controls) {
        controls.stop();
      }
    };
  }, []);

  // A handheld scanner types the code into the focused field and presses Enter.
  const handleHandheldKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (handheldCode.trim()) {
        onScanned(handheldCode.trim());
      }
    }
  };

  return (
    <DialogFrame title="Scan a QR or barcode" onDismiss={onCancel}>
      <Video ref={videoRef} muted playsInline hidden={cameraStatus === 'unavailable'} />
      <Status role="status" warning={cameraStatus === 'unavailable'}>{CAMERA_STATUS[cameraStatus]}</Status>
      <HandheldLabel htmlFor="handheld-scanner-input">Using a handheld scanner? Scan into this field.</HandheldLabel>
      <HandheldInput
        id="handheld-scanner-input"
        type="text"
        autoFocus
        autoComplete="off"
        placeholder="Waiting for a scan…"
        value={handheldCode}
        onChange={e => setHandheldCode(e.target.value)}
        onKeyDown={handleHandheldKeyDown}
      />
      <DialogButtons>
        <Button type="button" disabled={!handheldCode.trim()} onClick={() => onScanned(handheldCode.trim())}>Use code</Button>
        <Button secondary type="button" onClick={onCancel}>Cancel</Button>
      </DialogButtons>
    </DialogFrame>
  );
}

const ScanButton = styled(Button)`
  margin-top: 0.5rem;
`;

const Video = styled.video`
  display: ${props => props.hidden ? 'none' : 'block'};
  width: 100%;
  max-height: 16rem;
  background: #04040f;
  border-radius: 0.25rem;
  object-fit: cover;
`;

const Status = styled.p`
  margin: 0.5rem 0 1rem;
  font-size: 0.875rem;
  line-height: 1.4;
  color: ${props => props.warning ? '#b54708' : '#747474'};
`;

const HandheldLabel = styled.label`
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
`;

const HandheldInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem;
  font-size: 1rem;
  border: 0.0625rem solid #d4d4d4;
  border-radius: 0.25rem;
`;

export default ScannedCodeInput;
